import { redirect, type LoaderFunctionArgs } from "react-router-dom";
import { useAuthStore } from "@/features/auth";
import { useUserScopeStore } from "@/features/auth/model/user-scope.store";
import { userScopeApi } from "@/entities/auth";
import { useImpersonationStore } from "@/features/user/model/impersonationStore";
import { useWorkRoleStore } from "@/features/user/model/workRoleStore";
import { workContextToBackendRole } from "@/features/user/utils/role-mapping";
import { isSystemAdmin, isTechnicalUser, isTenantUser, isLocationUser } from "@/shared/types";

// ─── React hooks (used inside components) ────────────────────────────────────

/** Effective role: impersonated user's role when impersonating, else auth user's role */
export function useEffectiveRole(): string | null {
  const { user } = useAuthStore();
  const { isImpersonating, impersonatedContext } = useImpersonationStore();
  if (isImpersonating() && impersonatedContext) {
    return workContextToBackendRole(impersonatedContext);
  }
  return user?.role ?? null;
}

/** Effective location ID: impersonated user's branch when impersonating, else auth user's locationId */
export function useEffectiveLocationId(): string | null | undefined {
  const { user } = useAuthStore();
  const { isImpersonating, impersonatedContext } = useImpersonationStore();
  if (isImpersonating() && impersonatedContext?.branch_id) {
    return impersonatedContext.branch_id;
  }
  return user?.locationId ?? null;
}

// ─── Non-hook helpers (used inside loaders — no React context) ───────────────

/** Non-hook version of useEffectiveRole — safe to call in a loader */
function getEffectiveRole(): string | null {
  const { user } = useAuthStore.getState();
  const { isImpersonating, impersonatedContext } = useImpersonationStore.getState();
  if (isImpersonating() && impersonatedContext) {
    return workContextToBackendRole(impersonatedContext);
  }
  return user?.role ?? null;
}

/** Non-hook version of useEffectiveLocationId — safe to call in a loader */
function getEffectiveLocationId(): string | null | undefined {
  const { user } = useAuthStore.getState();
  const { isImpersonating, impersonatedContext } = useImpersonationStore.getState();
  if (isImpersonating() && impersonatedContext?.branch_id) {
    return impersonatedContext.branch_id;
  }
  return user?.locationId ?? null;
}

/**
 * Dashboard path for effective BRANCH_USER (restricted branch staff), aligned with login / dashboardLoader.
 * Returns undefined when the user should not be sent to that home (e.g. technicians, admins).
 */
export function getBranchUserDashboardPathIfApplicable(): string | undefined {
  const backendRole = getEffectiveRole();
  const locationId = getEffectiveLocationId();
  if (backendRole && isLocationUser(backendRole)) {
    return locationId ? `/branches/${locationId}` : "/technician-dashboard";
  }
  return undefined;
}

// ─── Path helpers (used by loaders + LayoutWrapper) ──────────────────────────

export const PATH_TO_MENU_KEY_MAP: Record<string, string> = {
  "/": "dashboard",
  "/technician-dashboard": "technicianDashboard",
  "/events": "events",
  "/branches": "branches",
  "/branches/categories": "branchCategories",
  "/assets": "assets",
  "/assets/categories": "assetCategories",
  "/assets/models": "assetModels",
  "/maintenance": "maintenance",
  "/inspections": "inspections",
  "/parts": "parts",
  "/warehouses": "warehouses",
  "/suppliers": "suppliers",
  "/tickets": "tickets",
  "/tickets/categories": "issueCategories",
  "/tickets/queues": "ticketQueues",
  "/analysis": "analysis",
  "/people": "people",
  "/users": "users",
  "/policies": "policies",
  "/settings": "settings",
};

/** Resolves a pathname to its sidebar menu key, handling dynamic segments. */
export function getMenuKeyFromPath(pathname: string): string | null {
  if (PATH_TO_MENU_KEY_MAP[pathname]) {
    return PATH_TO_MENU_KEY_MAP[pathname];
  }
  if (pathname.startsWith("/assets/")) {
    if (pathname.startsWith("/assets/categories")) return "assetCategories";
    if (pathname.startsWith("/assets/models")) return "assetModels";
    return "assets";
  }
  if (pathname.startsWith("/maintenance/")) return "maintenance";
  if (pathname.startsWith("/parts/")) return "parts";
  if (pathname.startsWith("/branches/")) {
    if (pathname.startsWith("/branches/categories")) return "branchCategories";
    return "branches";
  }
  if (pathname.startsWith("/suppliers/")) return "suppliers";
  if (pathname.startsWith("/tickets/")) {
    if (pathname.startsWith("/tickets/categories")) return "issueCategories";
    if (pathname.startsWith("/tickets/queues")) return "ticketQueues";
    return "tickets";
  }
  return null;
}

export function isPathAllowedForTechnicalRole(pathname: string): boolean {
  if (pathname === "/technician-dashboard") return true;
  if (pathname === "/tickets" || pathname.startsWith("/tickets/")) return true;

  // Entity detail pages (and their /label sub-route) may be deep-linked from a ticket.
  // List, category, model, and create sub-routes stay blocked.
  const detail = pathname.match(
    /^\/(assets|maintenance|parts|branches|events)\/([^/]+)(?:\/label)?$/
  );
  if (detail) {
    const reservedSegments = new Set(["create", "categories", "models"]);
    return !reservedSegments.has(detail[2]);
  }

  return false;
}

export function isPathAllowedForLocationUser(
  pathname: string,
  locationId: string | null | undefined
): boolean {
  if (!locationId) return false;
  const prefix = `/branches/${locationId}`;
  if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return true;
  if (pathname === "/tickets/new") return true;
  if (/^\/tickets\/[^/]+$/.test(pathname)) return true;
  return false;
}

// ─── Loader functions (replace wrapper component guards) ─────────────────────

export function protectedLoader(): Response | null {
  const { isAuthenticated } = useAuthStore.getState();
  if (!isAuthenticated) return redirect("/auth");
  return null;
}

export function publicOnlyLoader({ request }: LoaderFunctionArgs): Response | null {
  const { isAuthenticated, user } = useAuthStore.getState();
  const pathname = new URL(request.url).pathname;
  const isMagicLinkWithCode = /^\/magic-link\/[^/]+$/.test(pathname);

  if (isAuthenticated && user && !isMagicLinkWithCode) {
    if (isLocationUser(user.role)) {
      const to = user.locationId ? `/branches/${user.locationId}` : "/technician-dashboard";
      return redirect(to);
    }
    if (isSystemAdmin(user.role)) return redirect("/accounts");
    return redirect("/");
  }
  return null;
}

export function systemAdminLoader(): Response | null {
  const { isAuthenticated } = useAuthStore.getState();
  if (!isAuthenticated) return redirect("/auth");
  const effectiveRole = getEffectiveRole();
  if (!isSystemAdmin(effectiveRole)) return redirect("/");
  return null;
}

export function tenantUserLoader(): Response | null {
  const { isAuthenticated } = useAuthStore.getState();
  if (!isAuthenticated) return redirect("/auth");
  const effectiveRole = getEffectiveRole();
  if (isSystemAdmin(effectiveRole)) return redirect("/accounts");
  return null;
}

export function dashboardLoader(): Response | null {
  const { isAuthenticated } = useAuthStore.getState();
  if (!isAuthenticated) return redirect("/auth");
  const effectiveRole = getEffectiveRole();
  if (isSystemAdmin(effectiveRole)) return redirect("/accounts");
  if (isTechnicalUser(effectiveRole)) return redirect("/technician-dashboard");
  if (isLocationUser(effectiveRole)) {
    const locationId = getEffectiveLocationId();
    const to = locationId ? `/branches/${locationId}` : "/technician-dashboard";
    return redirect(to);
  }
  return null;
}

export async function scopeProtectedLoader({
  request,
}: LoaderFunctionArgs): Promise<Response | null> {
  const { isAuthenticated, user } = useAuthStore.getState();
  if (!isAuthenticated) return redirect("/auth");

  const effectiveRole = getEffectiveRole();
  if (isSystemAdmin(effectiveRole)) return redirect("/accounts");

  const pathname = new URL(request.url).pathname;

  if (isLocationUser(effectiveRole)) {
    const locationId = getEffectiveLocationId();
    if (locationId && !isPathAllowedForLocationUser(pathname, locationId)) {
      return redirect(`/branches/${locationId}`);
    }
    return null;
  }

  if (isTechnicalUser(effectiveRole)) {
    if (!isPathAllowedForTechnicalRole(pathname)) return redirect("/technician-dashboard");
    return null;
  }

  // Async: load scope if missing before rendering the page
  if (useUserScopeStore.getState().scope === null && user && isTenantUser(user.role)) {
    try {
      const loaded = await userScopeApi.buildUserScope();
      useUserScopeStore.getState().setScope(loaded);
    } catch {
      useUserScopeStore.getState().clearScope();
      return null; // allow access — API enforces security
    }
  }

  const { scope, canAccessMenuItem } = useUserScopeStore.getState();
  if (!scope) return null;

  const menuKey = getMenuKeyFromPath(pathname);
  if (!menuKey) return null;

  if (!canAccessMenuItem(menuKey)) {
    if (menuKey === "tickets" && useWorkRoleStore.getState().isBranchUser()) return null;
    return redirect("/");
  }

  return null;
}
